<template>
  <aside class="variablen-sidebar sidebar-fixed">
    <div id="select-variablen-sidebar-navbar-platzhalter" class="bg-sidebar border-start border-bottom"></div>

    <div class="bg-sidebar border-start border-top border-bottom shadow-sm px-1 py-2">
      <section id="select-variablen-sidebar-header">
        <p><strong>Variablen</strong></p>

        <div class="input-group input-group-sm mb-2">
          <input v-model.trim="searchString" type="search" class="form-control" id="searchInput" placeholder="Suche..." />
          <span class="input-group-text">
            <font-awesome-icon icon="magnifying-glass" class="p-1" />
          </span>
        </div>

        <div class="dropdown mb-2">
          <button
            class="btn btn-outline-secondary w-100 dropdown-toggle"
            type="button"
            id="select-herkunft-button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            Herkunft auswählen...
          </button>
          <div class="dropdown-menu p-0" aria-labelledby="select-herkunft-button">
            <div
              v-for="(table, index) in herkunft.sort((a: any, b: any) => a.key.localeCompare(b.key))"
              :key="index"
              class="dropdown-checkbox px-1 py-2 border-bottom"
            >
              <div class="form-check">
                <input class="form-check-input" type="checkbox" :id="'selected-herkunft-item-' + index" v-model="selectedHerkunft" :value="table" />
                <label class="form-check-label" :for="'selected-herkunft-item-' + index">{{ table.key != '' ? table.key : 'Unbekannt' }}</label>
              </div>
            </div>
          </div>
        </div>

        <div v-if="selectedHerkunft.length > 0" class="d-flex mx-1 mb-2">
          <small class="text-muted">
            Filter: <span v-for="filter in selectedHerkunft" :key="filter" class="me-2">{{ filter.key }} </span>
          </small>
        </div>
      </section>

      <div class="border-top py-2">
        <LoadingSpinner v-if="getVariablenLoading" />

        <div v-else-if="variablenErrorMsg != ''" class="alert alert-danger">
          {{ variablenErrorMsg }}
        </div>

        <div v-for="(value, key) in groupedItems" :key="key" class="mb-3">
          <p class="mb-1">
            <strong>{{ key }}</strong>
          </p>
          <div
            v-for="(variable, idx) in value"
            :key="variable.id"
            :id="'variable-' + key + '-' + idx"
            class="mb-1 border p-2 bg-white shadow-sm variable-content"
            :title="variable.platzhalter"
            @click="copyToClipboard(variable.platzhalter, key, idx)"
          >
            {{ variable.platzhalter }}
          </div>
        </div>

        <div v-if="!getVariablenLoading" class="mb-1">
          <EditVariableModal />
        </div>
      </div>
    </div>
  </aside>
</template>

<script lang="ts" setup>
  //#region Imports
  import { ref, computed } from 'vue';
  import { useStore } from '@/store';

  import EditVariableModal from '@/components/Vorlagen/EditVariableModal.vue';
  import LoadingSpinner from '@/components/LoadingSpinner.vue';

  import { Variable, GroupedVariablen } from '@/models/VorlageModels';
  //#endregion

  const store = useStore();

  const getVariablenLoading = computed(() => store.getters.status.getVariablenLoading);
  const variablenErrorMsg = computed(() => store.getters.status.variablenErrorMsg);

  const variablen = computed(() => store.getters.variablen);

  const searchString = ref('');
  const selectedHerkunft = ref<Array<any>>([]);
  const herkunft = [
    { key: 'Keine', value: '' },
    { key: 'Personaldaten', value: 'Personaldaten' },
    { key: 'Vertrag', value: 'Vertrag' },
  ];

  const sortedVariablen = computed(() => {
    return variablen.value
      .filter((v: Variable) => (selectedHerkunft.value.length > 0 ? selectedHerkunft.value.map((h) => h.value).includes(v.herkunft) : true))
      .filter((v: Variable) => {
        return (
          v.titel.trim().toLowerCase().includes(searchString.value.trim().toLowerCase()) ||
          v.gruppe.trim().toLowerCase().includes(searchString.value.trim().toLowerCase())
        );
      })
      .sort((a: Variable, b: Variable) => {
        var titleComparison = a.titel.trim().toLowerCase().localeCompare(b.titel.trim().toLowerCase());
        return titleComparison;
      });
  });

  const groupedItems = computed<GroupedVariablen>(() => {
    const noGroup: Variable[] = [];

    const data = sortedVariablen.value.reduce((groups: GroupedVariablen, variable: Variable) => {
      if (!variable.gruppe) {
        noGroup.push(variable);
      } else {
        const groupsList = variable.gruppe.split(';');

        for (let group of groupsList) {
          group = group.trim();

          if (group === '') {
            continue;
          }
          const existingGroup = groups[group];

          if (existingGroup) {
            existingGroup.push(variable);
          } else {
            groups[group] = [variable];
          }
        }
      }
      return groups;
    }, {});

    // Sort keys alphabetically
    const sortedData: GroupedVariablen = {};
    Object.keys(data)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .forEach((key) => {
        sortedData[key] = data[key];
      });

    if (noGroup.length > 0) {
      sortedData['Keine Gruppe'] = noGroup;
    }

    return sortedData;
  });

  function copyToClipboard(text: string, key: string | number, idx: number) {
    navigator.clipboard.writeText(text);

    const id = 'variable-' + key + '-' + idx;
    const currentElement = document.getElementById(id) as HTMLElement;

    // Save the original text
    const originalText = currentElement.textContent;

    // Add class and new text
    currentElement.classList.add('variable-content-copy', 'text-center');
    currentElement.textContent = 'Variable kopiert!';

    // Remove class and restore text after 4 seconds
    setTimeout(() => {
      currentElement.classList.remove('variable-content-copy');
      currentElement.classList.remove('text-center');
      currentElement.textContent = originalText;
    }, 2000); // 4000 milliseconds = 4 seconds
  }
</script>

<style scoped>
  .sidebar-fixed {
    position: fixed;
    top: 0; /* falls du eine fixed Navbar hast, z.B. top: 56px; */
    right: 0;
    bottom: 65px;
    overflow-y: auto; /* eigene Scrollbar */
    /* padding: 1rem; */
  }

  .bg-sidebar {
    background-color: #f8f8f8;
  }

  #select-variablen-sidebar-navbar-platzhalter {
    height: 61px;
    margin-bottom: 4px;
  }

  .variablen-sidebar {
    width: 250px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }

  .variable-content {
    cursor: pointer;
    user-select: none;
  }

  .variable-content:hover {
    background-color: #fff4af !important;
  }

  .variable-content-copy {
    background-color: #fff4af !important;
  }
</style>
